import { useEffect, useState, useRef } from "react";
import { auth } from "../firebase/firebase-config";
import {
    onAuthStateChanged,
    getIdToken,
    User as FirebaseUser,
} from "firebase/auth";
import axios from "axios";
import useAppDispatch from "../hooks/useAppDispatch";
import {
    setUser,
    setRole,
    setIsOnboarded,
    setIsAdminView,
    setLoading,
    setError,
    setEmailVerified,
    setIsActive,
    setIsSubscribed,
    setMaintenance,
    setSocialsConnected,
    setAdmin,
    setListingCreated,
    startImpersonation,
    setMaintenanceMessage
} from "../store/authSlice";
import { addImage, getImage } from "../utils/dexieImageCache";

const useAuth = () => {
    const dispatch = useAppDispatch();
    const [authCheckCompleted, setAuthCheckCompleted] = useState(false);
    const impersonationRestoredRef = useRef(false);
    const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(
            auth,
            async (currentUser: FirebaseUser | null) => {
                dispatch(setLoading(true));
                dispatch(setError(null));

                if (currentUser) {
                    try {
                        const token = await getIdToken(
                            currentUser,
                            true
                        );
                        const authPayload = {
                            authId: currentUser.uid,
                            token,
                        };
                        const response = await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/validateUser`,
                            authPayload,
                            {
                                headers: {
                                    "Content-Type": "application/json",
                                },
                            }
                        );

                        if (!response.data || !response.data.user) {
                            throw new Error("User validation failed");
                        }

                        const userId =
                            response.data.user._id?.toString();

                        // Backend stores a GCS key in `profileImage` (or unsets
                        // it). The transformer converts that key to a signed
                        // URL before responding, so what we see here is either
                        // a signed URL (custom upload) or undefined.
                        // We cache the resolved blob (federated or signed) in
                        // IDB under a per-user key for offline-friendly renders.
                        const imageUrlToCache = response.data.user.profileImage || currentUser.photoURL;

                        if (imageUrlToCache && userId) {
                            const cacheKey = `profileImage-${userId}`;
                            try {
                                const cachedBlob = await getImage(cacheKey);
                                if (!cachedBlob) {
                                    const imgFetchResponse = await fetch(
                                        imageUrlToCache,
                                        {
                                            cache: "no-cache",
                                        }
                                    );

                                    if (imgFetchResponse.status === 429) {
                                        console.error(
                                            "Rate limited while fetching profile image."
                                        );
                                    } else if (imgFetchResponse.ok) {
                                        const blob =
                                            await imgFetchResponse.blob();
                                        await addImage(
                                            cacheKey,
                                            blob
                                        );
                                    } else {
                                        console.error(
                                            "Failed to fetch image:",
                                            imgFetchResponse.statusText
                                        );
                                    }
                                }
                            } catch (imageError) {
                                console.error(
                                    "Image caching failed:",
                                    imageError
                                );
                            }
                        }

                        const userRole =
                            response.data.user.role || "USER";
                        const userData = {
                            ...response.data.user,
                            uid: currentUser.uid,
                            email: currentUser.email,
                            displayName: currentUser.displayName,
                            photoURL: currentUser.photoURL,
                            emailVerified: currentUser.emailVerified,
                            providerId:
                                currentUser.providerData[0]
                                    ?.providerId || "",
                            phoneNumber:
                                response.data.user.phoneNumber || "",
                        };
                        dispatch(setUser(userData));
                        dispatch(
                            setSocialsConnected(
                                userData.socialsConnected || false
                            )
                        );
                        dispatch(setRole(userRole));
                        dispatch(
                            setIsOnboarded(
                                (response.data.user.onboarded &&
                                    response.data.user.status !==
                                        "ONBOARDING") ||
                                    false
                            )
                        );
                        dispatch(
                            setEmailVerified(
                                currentUser.emailVerified || false
                            )
                        );
                        dispatch(
                            setIsActive(
                                (response.data.user.status !==
                                    "PAUSED" &&
                                    response.data.user.status !==
                                        "INACTIVE") ||
                                    false
                            )
                        );
                        dispatch(
                            setIsSubscribed(
                                response.data.user.account
                                    .activeSubscription.status ===
                                    "ACTIVE" || false
                            )
                        );
                        dispatch(
                            setMaintenance(
                                response.data.maintenance || false
                            )
                        );
                        dispatch(
                            setMaintenanceMessage(
                                response.data.maintenanceMessage || null
                            )
                        );
                        dispatch(
                            setListingCreated(
                                response.data.user.listingCreated ||
                                    false
                            )
                        );

                        // Automatically set `isAdminView` to true for admins and store in sessionStorage if not already present
                        const localIsAdminView =
                            sessionStorage.getItem("isAdminView");

                        dispatch(setAdmin(response.data.user.isAdmin));

                        if (localIsAdminView === null) {
                            // Set admin view for both regular admins and CREATOR users
                            if (response.data.user.isAdmin || response.data.user.adminRole === "CREATOR") {
                                dispatch(setIsAdminView(true));
                                sessionStorage.setItem(
                                    "isAdminView",
                                    "true"
                                );
                            } else {
                                dispatch(setIsAdminView(false));
                                sessionStorage.setItem(
                                    "isAdminView",
                                    "false"
                                );
                            }
                        } else {
                            // Check if user is CREATOR and there's evidence of impersonation cleanup
                            const refreshOriginalUser = sessionStorage.getItem("refreshOriginalUser");
                            const isCreatorAfterImpersonation = response.data.user.adminRole === "CREATOR" && refreshOriginalUser;
                            
                            // Allow admin view for regular admins OR CREATOR users (with current sessionStorage) OR after impersonation cleanup
                            dispatch(
                                setIsAdminView(
                                    (localIsAdminView === "true" && 
                                        (response.data.user.isAdmin || response.data.user.adminRole === "CREATOR")) || 
                                    isCreatorAfterImpersonation
                                )
                            );
                        }
                    } catch (error) {
                        console.error("User validation error:", error);
                        dispatch(
                            setError(
                                "User validation failed. Please try again later."
                            )
                        );
                        dispatch(setUser(null));
                        dispatch(setRole(null));
                        dispatch(setIsOnboarded(false));
                        dispatch(setIsAdminView(false));
                    }
                } else {
                    dispatch(setUser(null));
                    dispatch(setRole(null));
                    dispatch(setIsOnboarded(false));
                    dispatch(setIsAdminView(false));
                }

                // Ensure loading is false after the process is complete
                if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
                settleTimerRef.current = setTimeout(() => {
                    dispatch(setLoading(false));
                    setAuthCheckCompleted(true);
                    
                    // Restore impersonation state from sessionStorage after auth is complete
                    if (!impersonationRestoredRef.current) {
                        const storedIsImpersonating = sessionStorage.getItem("isImpersonating");
                        const storedImpersonatedUser = sessionStorage.getItem("impersonatedUser");
                        const storedImpersonationToken = sessionStorage.getItem("impersonationToken");
                        const storedSessionId = sessionStorage.getItem("sessionId");
                        const storedOriginalUser = sessionStorage.getItem("originalUser");
                        const refreshOriginalUser = sessionStorage.getItem("refreshOriginalUser");                               
                        const originalUserToCheck = refreshOriginalUser || storedOriginalUser;

                        if (originalUserToCheck) {
                            try {
                                const originalUser = JSON.parse(originalUserToCheck);
                                if (originalUser.adminRole === "CREATOR" || originalUser.isAdmin) {
                                    
                                    // Clean up ALL impersonation data FIRST to prevent restoration
                                    sessionStorage.removeItem("isImpersonating");
                                    sessionStorage.removeItem("impersonatedUser");
                                    sessionStorage.removeItem("originalUser");
                                    sessionStorage.removeItem("refreshOriginalUser");
                                    sessionStorage.removeItem("impersonationToken");
                                    sessionStorage.removeItem("sessionId");
                                    sessionStorage.removeItem("wasAdminView");
                                    
                                    // CREATOR/Admin users should go back to admin mode after refresh
                                    dispatch(setIsAdminView(true));
                                    sessionStorage.setItem("isAdminView", "true");
                                    
                                    impersonationRestoredRef.current = true;
                                    return; // Exit early, don't restore impersonation
                                }
                            } catch (e) {
                                console.error("Failed to parse original user:", e);
                            }
                        }
                        
                        if (storedIsImpersonating === "true" && storedImpersonatedUser && storedImpersonationToken && storedSessionId) {
                            try {
                                const impersonatedUser = JSON.parse(storedImpersonatedUser);
                                dispatch(startImpersonation({
                                    user: impersonatedUser,
                                    impersonationToken: storedImpersonationToken,
                                    sessionId: storedSessionId
                                }));
                                // Switch to user mode when restoring impersonation
                                dispatch(setIsAdminView(false));
                                impersonationRestoredRef.current = true;
                            } catch (e) {
                                console.error("Failed to restore impersonation state:", e);
                                sessionStorage.removeItem("isImpersonating");
                                sessionStorage.removeItem("impersonatedUser");
                                sessionStorage.removeItem("originalUser");
                                sessionStorage.removeItem("wasAdminView");
                                sessionStorage.removeItem("impersonationToken");
                                sessionStorage.removeItem("sessionId");
                            }
                        }
                    }
                }, 500);
            }
        );

        return () => {
            unsubscribe();
            if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
        };
    }, [dispatch]);

    return { authCheckCompleted };
};

export default useAuth;
